import PlaceModel              from '@models/map/PlaceModel';
import Form                    from '@widesk-ui/components/form/Form';
import Input                   from '@widesk-ui/components/form/Input';
import useApiCollection        from '@widesk-core/hooks/useApiCollection';
import PlaceTypeFieldModel     from '@models/map/PlaceTypeFieldModel';
import { FormItem }            from '@widesk-ui/components/form/FormItem';
import PlaceFieldModel         from '@models/map/PlaceFieldModel';
import ApiCollection           from '@widesk-core/models/ApiCollection';
import ColorPicker             from '@widesk-ui/components/form/ColorPicker';
import MultiLangHorizontalTabs from '@widesk-ui/components/MultiLangHorizontalTabs';
import languages               from '@/languages';
import TextArea                from '@widesk-ui/components/form/TextArea';
import InputNumber             from '@widesk-ui/components/form/InputNumber';
import CheckboxGroup           from '@widesk-ui/components/form/CheckboxGroup';
import Alert                   from '@widesk-ui/components/Alert';
import Button                  from '@widesk-ui/components/Button';
import { useState }            from 'react';
import React                   from 'react';
import View                    from '@widesk-ui/components/View';
import { CheckSquareOutlined } from '@ant-design/icons';
import { CloseSquareOutlined } from '@ant-design/icons';
import Value                   from '@widesk-ui/components/Value';
import InputUrl                from '@widesk-ui/components/form/InputUrl';
import ImageUrlInput           from '@/TokamapEditor/ux/ImageUrlInput';
import toTranslationsPayload   from '@/helpers/toTranslationsPayload';

export type PlaceFormProps = {
	models: PlaceModel[];
	onSuccess?: () => void;
}

type PlaceTypeFieldInputsProps = {
	selectedPlaceTypeFieldIds: id[];
	collPlaceTypeField: ApiCollection<PlaceTypeFieldModel>;
	collPlaceField: ApiCollection<PlaceFieldModel>;
};

const PlaceTypeFieldInputs = (
	{ collPlaceTypeField, collPlaceField, selectedPlaceTypeFieldIds }: PlaceTypeFieldInputsProps,
) => {
	return collPlaceTypeField.filterByIncluded('id', selectedPlaceTypeFieldIds).map(placeTypeField => {
		const field = placeTypeField.field;
		const typeReference = field.fieldType.reference;

		const placeFields = collPlaceField.filterByResolvableId('field', field.id);

		if (typeReference === 'translatable') {
			return (
				<MultiLangHorizontalTabs items={languages.map(lang => ({
					formItems: () => [
						<FormItem
							label={placeTypeField.field.label}
							name={[`placeTypeField.${placeTypeField.id}`, lang, 'value']}
							extra={
								<>
									Valeurs de la sélection :
									<Value value={placeFields.map(pf => pf.translation('value', lang)).filter(Boolean)} />
								</>
							}
						>
							<TextArea placeholder={`${field.label} - ${lang}`} />
						</FormItem>,
					],
					lang,
				}))} />
			);
		}

		const placeFieldValues = placeFields.map(placeField => placeField.value).join(', ');

		let FieldTypeInput;
		switch (typeReference) {
			case 'image':
				FieldTypeInput = <ImageUrlInput />;
				break;

			case 'number':
				FieldTypeInput = <InputNumber addonBefore="Nombre" placeholder={field.label} />;
				break;

			case 'url':
				FieldTypeInput = <InputUrl placeholder={field.label} />;
				break;

			case 'color':
				FieldTypeInput = <ColorPicker />;
				break;

			case 'string':
				FieldTypeInput = <TextArea placeholder={field.label} />;
				break;

			default:
				FieldTypeInput = <Input placeholder={field.label} />;
		}
		return (
			<FormItem
				key={placeTypeField.id}
				name={`placeTypeField.${placeTypeField.id}`}
				label={placeTypeField.field.label}
				extra={
					<>Valeurs de la sélection : <Value value={placeFieldValues} /></>
				}
			>
				{FieldTypeInput}
			</FormItem>
		);
	});
};

export default function MultiplePlaceForm({
	models,
	onSuccess,
}: PlaceFormProps) {
	const collPlace = useApiCollection(PlaceModel);
	const collPlaceField = useApiCollection(PlaceFieldModel);
	const collPlaceTypeField = useApiCollection(PlaceTypeFieldModel);
	const [selectedPlaceTypeFieldIds, setSelectedPlaceTypeFieldIds] = useState<id[]>([]);

	const allSelected = React.useMemo(() => {
		return !!collPlaceTypeField.ids.length
			&& selectedPlaceTypeFieldIds.length === collPlaceTypeField.ids.length;
	}, [selectedPlaceTypeFieldIds, collPlaceTypeField]);

	return (
		<Form
			onChange={v => console.log(v.getValues())}
			gap="md"
			fields={formTools => ({
				info: {
					noFormItem: true,
					element: <Alert
						description={<>
							<div>
								Les <strong>valeurs saisies</strong> pour chaque champ sélectionné
								<strong> remplaceront</strong> les valeurs des points d'intérêt sélectionnés.
							</div>
							<div style={{ marginBlock: 8 }}>
								Laisser <strong>un champ vide supprimera </strong>
								la valeur de ce champ dans chaque point d'intérêt.
							</div>
						</>}
						showIcon
					/>,
					span: 24,
				},
				selectedPlaceTypeField: {
					extra: (
						<View left mt="xs">
							<Button
								danger={allSelected}
								icon={allSelected ? <CloseSquareOutlined /> : <CheckSquareOutlined />}
								onClick={() => {
									const newValue = allSelected ? [] : collPlaceTypeField.ids;
									setSelectedPlaceTypeFieldIds(newValue);
									formTools.setFieldValue('selectedPlaceTypeField', newValue);
								}}
							>
								Tout {selectedPlaceTypeFieldIds.length === collPlaceTypeField.ids.length ? 'dé' : ''}sélectionner
							</Button>
						</View>
					),
					label: 'Choisissez les champs à remplacer',
					element: (
						<CheckboxGroup
							onChange={(v: any) => setSelectedPlaceTypeFieldIds(v)}
							options={collPlaceTypeField.map(ptf => ({
								label: ptf.field.label,
								value: ptf.id,
							}))}
							style={{ flexDirection: 'column' }}
						/>
					),
					span: 24,
					required: true,
				},
			})}
			onSuccess={onSuccess}
			onDidMount={async () => {
				models.map(m => collPlace.add(m));
				const placeTypeId = models[0]?.getId('placeType');

				await collPlaceField
					.setLocale('*')
					.listBy(collPlace.ids, 'place');
				await collPlaceTypeField
					.setSort('position', 'desc')
					.listBy([placeTypeId], 'placeType');
				await collPlaceTypeField.whenIsLoaded(ptf => ptf.field.fieldType);
			}}
			successMessage={'test'}
			onSubmit={async (values) => {
				await Promise.all(
					collPlace.map(place => {
						return collPlaceTypeField.map(placeTypeField => {
							const placeField = collPlaceField.find(pf => {
								return pf.getId('field') === placeTypeField.getId('field')
									&& pf.getId('place') === place.id;
							}) || new PlaceFieldModel();

							const value = (values as any)[`placeTypeField.${placeTypeField.id}`] || null;
							if (value === null) {
								if (placeField.id) {
									return placeField.destroy();
								}

								return null;
							}

							const valueAttribute: Partial<Record<'value' | 'translations', any>> = {};
							if (placeTypeField.field.fieldType.reference === 'translatable') {
								valueAttribute['translations'] = toTranslationsPayload(value);
							} else {
								valueAttribute['value'] = value?.toString();
							}

							return placeField.patch({
								place: place.urn,
								field: placeTypeField.getUrn('field'),
								...valueAttribute,
							});
						});
					}),
				);

				return models.at(0);
			}}
		>
			<PlaceTypeFieldInputs
				collPlaceField={collPlaceField}
				collPlaceTypeField={collPlaceTypeField}
				selectedPlaceTypeFieldIds={selectedPlaceTypeFieldIds}
			/>
		</Form>
	);
}